import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TextField from './fields/TextField';
import RichTextField from './fields/RichTextField';
import MediaField from './fields/MediaField';
import HeroHomeEditor from './editors/HeroHomeEditor';
import NewsPageEditor from './editors/NewsPageEditor';
import ArticleSubmissionEditor from './editors/ArticleSubmissionEditor';
import ArticleSubmitEditor from './editors/ArticleSubmitEditor';
import {
  HomeMainEditor,
  HomeAboutEditor,
  HomeInvitationEditor,
  HomeParticipationEditor,
  HomeIndexingEditor,
  HomeDatesEditor,
  HomeTopicsEditor,
} from './editors/HomeSectionEditors';
import './ContentEditor.css';

const HOME_EDITORS = {
  HERO: HeroHomeEditor,
  MAIN: HomeMainEditor,
  ABOUT: HomeAboutEditor,
  INVITATION: HomeInvitationEditor,
  PARTICIPATION: HomeParticipationEditor,
  INDEXING: HomeIndexingEditor,
  DATES: HomeDatesEditor,
  TOPICS: HomeTopicsEditor,
};

const NEWS_EDITORS = {
  CONTENT: NewsPageEditor,
};

const ARTICLE_SUBMISSION_EDITORS = {
  CONTENT: ArticleSubmissionEditor,
};

const ARTICLE_SUBMIT_EDITORS = {
  CONTENT: ArticleSubmitEditor,
};

/** Подмешивает несохранённые правки из `changes` — иначе value в input не меняется (controlled). */
function mergeComponentContentChanges(originalContent, componentId, changes) {
  const compChanges = changes[componentId];
  if (!compChanges) {
    if (originalContent == null) return {};
    if (typeof originalContent === 'string') {
      try {
        return JSON.parse(originalContent);
      } catch {
        return {};
      }
    }
    return originalContent;
  }

  let base;
  try {
    base =
      typeof originalContent === 'string'
        ? JSON.parse(originalContent || '{}')
        : JSON.parse(JSON.stringify(originalContent || {}));
  } catch {
    base = {};
  }

  Object.entries(compChanges).forEach(([pathStr, value]) => {
    if (pathStr === '__mediaId__') return;
    const path = pathStr.split('.');
    let current = base;
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
      const nextKey = path[i + 1];
      if (current[key] === undefined || current[key] === null) {
        current[key] = /^\d+$/.test(String(nextKey)) ? [] : {};
      }
      current = current[key];
    }
    current[path[path.length - 1]] = value;
  });

  return base;
}

const ContentEditor = ({
  components,
  languages,
  onUpdate,
  systemKey,
  changes = {},
  token,
}) => {
  const renderValueField = (componentId, path, value, key) => {
    // Determine field type based on key or value
    const pathStr = path.join('.');
    const isEmpty = !value || (typeof value === 'string' && value.trim() === '');

    const commonProps = {
      value: value || '',
      onChange: (newValue) => onUpdate(componentId, path, newValue),
      isEmpty,
      hasTranslation: true, // В новой архитектуре редактируем локализованные поля напрямую
    };

    // Heuristics for field types
    if (key.toLowerCase().includes('image') || key.toLowerCase().includes('video') || key.toLowerCase().includes('media')) {
      return (
        <MediaField
          {...commonProps}
          type={key.toLowerCase().includes('video') ? 'VIDEO' : 'IMAGE'}
          apiUrl={process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}
          token={localStorage.getItem('admin_token')}
        />
      );
    }

    if (key.toLowerCase().includes('description') || key.toLowerCase().includes('text') || key.toLowerCase().includes('content')) {
      return <RichTextField {...commonProps} />;
    }

    return <TextField {...commonProps} />;
  };

  const renderMultiLangField = (componentId, path, valueObj, key) => {
    return (
      <div className="multi-lang-field">
        {languages.map((lang) => (
          <div key={lang.code} className="multi-lang-row">
            <label className="multi-lang-label">
              {lang.code.toUpperCase()}
            </label>
            {renderValueField(
              componentId,
              [...path, lang.code],
              valueObj?.[lang.code] || '',
              key,
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderContentObject = (componentId, obj, currentPath = []) => {
    if (!obj || typeof obj !== 'object') return null;

    return Object.entries(obj).map(([key, value]) => {
      const newPath = [...currentPath, key];

      const isTranslationObj =
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        languages.some((l) => Object.prototype.hasOwnProperty.call(value, l.code));

      if (isTranslationObj) {
        return (
          <div key={newPath.join('.')} className="content-item">
            <div className="item-header">
              <div className="item-key">
                <code>{key}</code>
              </div>
            </div>
            <div className="item-field">
              {renderMultiLangField(componentId, newPath, value, key)}
            </div>
          </div>
        );
      }

      // Если это просто вложенный объект, рекурсивно отображаем его
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return (
          <div key={newPath.join('.')} className="nested-content-group">
            <h4 className="nested-group-title">{key}</h4>
            {renderContentObject(componentId, value, newPath)}
          </div>
        );
      }

      // Если это простое значение (например, ссылка или нулокализованный текст)
      if (typeof value !== 'object') {
        return (
          <div key={newPath.join('.')} className="content-item">
            <div className="item-header">
              <div className="item-key">
                <code>{key}</code>
              </div>
            </div>
            <div className="item-field">
              {renderValueField(componentId, newPath, value, key)}
            </div>
          </div>
        );
      }

      return null;
    });
  };

  return (
    <div className="content-editor">
      <AnimatePresence mode="wait">
        {components.length === 0 ? (
          <motion.div
            key="empty"
            className="empty-state"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <p>Компоненты не найдены</p>
          </motion.div>
        ) : (
          <motion.div
            className="components-list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {components.map((comp) => {
              const Block =
                systemKey === 'home'
                  ? HOME_EDITORS[comp.type]
                  : systemKey === 'news'
                    ? NEWS_EDITORS[comp.type]
                    : systemKey === 'articleSubmission'
                      ? ARTICLE_SUBMISSION_EDITORS[comp.type]
                      : systemKey === 'articleSubmit'
                        ? ARTICLE_SUBMIT_EDITORS[comp.type]
                        : null;
              if (Block) {
                return (
                  <Block
                    key={comp.id}
                    component={comp}
                    languages={languages}
                    changes={changes}
                    onUpdate={onUpdate}
                    token={token || localStorage.getItem('admin_token')}
                  />
                );
              }

              const content = mergeComponentContentChanges(comp.content, comp.id, changes);

              return (
                <div key={comp.id} className="component-group">
                  <div className="content-items">
                    {renderContentObject(comp.id, content)}
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContentEditor;
